import React from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/platform/Badges";
import { fmtD } from "@/lib/platform";
import { scoreTone } from "@/lib/vendors";
import { ContractBadge, RiskBadge } from "@/components/vendors/VendorBadges";

export const ALL = "__all";

const Filter = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-[165px] text-xs bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const InventoryFilters = ({ f, set, meta }) => (
  <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-xl p-3" data-testid="vendor-filters">
    <div className="relative">
      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
      <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Name, ref, service" className="h-9 pl-8 w-[210px] text-xs"
             data-testid="vendor-search-input" />
    </div>
    <Filter value={f.risk} onChange={set("risk")} placeholder="All final risk" options={["High", "Medium", "Low"].map((x) => [x, x])} testId="vendor-filter-risk" />
    <Filter value={f.contract_status} onChange={set("contract_status")} placeholder="All contracts" options={Object.entries(meta.contract_statuses)} testId="vendor-filter-contract" />
    <Filter value={f.service_type} onChange={set("service_type")} placeholder="All services" options={meta.service_types.map((s) => [s, s])} testId="vendor-filter-service" />
    <Filter value={f.hosting} onChange={set("hosting")} placeholder="All hosting" options={Object.entries(meta.hosting)} testId="vendor-filter-hosting" />
    <Filter value={f.assessment} onChange={set("assessment")} placeholder="Any assessment state" testId="vendor-filter-assessment"
            options={[["overdue", "Overdue"], ["due_soon", "Due in 30 days"], ["never", "Never assessed"], ["in_progress", "In progress"]]} />
    <Filter value={f.status} onChange={set("status")} placeholder="All statuses" options={Object.entries(meta.statuses)} testId="vendor-filter-status" />
  </div>
);

const Row = ({ v, onOpen }) => (
  <tr onClick={onOpen} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer transition-colors" data-testid={`vendor-row-${v.id}`}>
    <td className="px-4 py-3">
      <div className="font-medium text-[#003366]">{v.name}</div>
      <div className="text-[11px] text-slate-500"><span className="font-mono text-accent-sm">{v.vendor_id}</span> · {v.service_type}</div>
    </td>
    <td className="px-4 py-3 text-xs text-slate-600">{v.country}<div className="text-[10px] text-accent-sm">{v.hosting_effective.toLowerCase()}</div></td>
    <td className="px-4 py-3 text-xs font-mono">
      <span className="text-slate-700">{v.risk.inherent_risk_score}</span>
      <div><RiskBadge level={v.inherent_risk_rating} /></div>
    </td>
    <td className={`px-4 py-3 text-xs font-mono ${scoreTone(v.latest_assessment_score)}`}>{v.latest_assessment_score == null ? "—" : `${v.latest_assessment_score}%`}</td>
    <td className="px-4 py-3 text-xs font-mono">
      <span className="text-slate-700">{v.risk.residual_risk_score}</span>
      <div><RiskBadge level={v.risk_level} testId={`vendor-final-risk-${v.id}`} /></div>
    </td>
    <td className="px-4 py-3"><ContractBadge status={v.contract_status} label={v.contract_status_label} /></td>
    <td className="px-4 py-3 text-xs">
      <span className={`font-mono ${v.assessment_overdue ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtD(v.next_assessment_due)}</span>
      {v.active_review && <div className="text-[10px] text-[#008080]">{v.active_review.status_label}</div>}
    </td>
    <td className="px-4 py-3 text-xs text-slate-700">{v.owner}</td>
  </tr>
);

export const VendorInventory = ({ rows, loading }) => {
  const nav = useNavigate();
  if (!loading && rows.length === 0) return <EmptyState icon={Building2} title="No vendors match" hint="Add a vendor or clear the filters." testId="vendor-empty-state" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="vendor-table">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
            {["Vendor", "Country", "Inherent", "Assessment", "Residual / final", "Contract", "Next assessment", "Owner"].map((h) => (
              <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map((v) => <Row key={v.id} v={v} onOpen={() => nav(`/vendors/${v.id}`)} />)}</tbody>
      </table>
    </div>
  );
};

export const VendorPagination = ({ page, limit, total, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (total === 0) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between text-xs text-slate-500 px-1" data-testid="vendor-pagination">
      <div data-testid="vendor-pagination-summary">Showing {start}–{end} of {total} vendors</div>
      <div className="flex items-center gap-2">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                data-testid="vendor-pagination-prev">Prev</button>
        <span data-testid="vendor-pagination-page">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                data-testid="vendor-pagination-next">Next</button>
      </div>
    </div>
  );
};
